import {
  ArrowPathIcon,
  ArrowsRightLeftIcon,
  BuildingOffice2Icon,
  CalendarIcon,
  ChatBubbleLeftRightIcon,
  CircleStackIcon,
  ClipboardDocumentListIcon,
  CloudArrowUpIcon,
  CubeIcon,
  CurrencyDollarIcon,
  EnvelopeIcon,
  FunnelIcon,
  PencilSquareIcon,
  SparklesIcon,
  TagIcon,
  UserIcon,
  UserPlusIcon,
} from "@heroicons/react/24/outline";
import { getBaseUrl } from "../../api/api";
import { RuleItemTypes, RuleOperators } from "../../constants/action-types";
import { SlackSVG } from "../../icons/SlackSVG";

type RuleItemAppIconProps = {
  data?: {
    type?: string;
    inputType?: string;
    operator?: string;
    fieldId?: string;
    aiFieldAction?: string;
  };
  itemType?: string;
};

export default function RuleItemAppIcon({ data = {}, itemType }: RuleItemAppIconProps) {
  const appUrl = getBaseUrl();
  const type = data.type;
  const inputType = data.inputType;
  const operator = data.operator;
  const fieldId = data.fieldId;
  const aiFieldAction = data.aiFieldAction;

  // ── AI_FIELD actions: use specific AI icons ──
  if (type === "AI_FIELD") {
    // All AI actions use SparklesIcon but with different sub-icons could be added later
    if (aiFieldAction === "researchRecord") return <SparklesIcon className="w-4" />;
    if (aiFieldAction === "summarizeRecord") return <ClipboardDocumentListIcon className="w-4" />;
    if (aiFieldAction === "classifyRecord") return <TagIcon className="w-4" />;
    if (aiFieldAction === "classifyText") return <TagIcon className="w-4" />;
    if (aiFieldAction === "promptCompletion") return <ChatBubbleLeftRightIcon className="w-4" />;
    return <SparklesIcon className="w-4" />;
  }

  // ── Actions: operator-based icons ──
  if (itemType === RuleItemTypes.ACTION) {
    // SET_FIELD: differentiate by fieldId
    if (operator === RuleOperators.SET_FIELD) {
      if (fieldId === "bucketId") return <FunnelIcon className="w-4" />; // move to funnel
      if (fieldId === "status") return <ArrowPathIcon className="w-4" />; // change status
      if (fieldId === "assignees") return <UserPlusIcon className="w-4" />; // assign person
      return <PencilSquareIcon className="w-4" />; // update column (generic)
    }

    // Convert actions
    if (operator === RuleOperators.CONTACT_2_NEW_SALE)
      return <ArrowsRightLeftIcon className="w-4" />;
    if (operator === RuleOperators.JIRA_CUSTOMER_2_NEW_CONTACT)
      return <ArrowsRightLeftIcon className="w-4" />;
    if (operator === RuleOperators.HUBSPOT_2_NEW_SALE)
      return <ArrowsRightLeftIcon className="w-4" />;

    // Send email
    if (operator === RuleOperators.SEND_EMAIL) return <EnvelopeIcon className="w-4" />;

    // Slack
    if (operator === RuleOperators.SEND_TO_SLACK_CHANNEL) {
      return <SlackSVG className="w-4 rounded-full" />;
    }

    // Mailchimp
    if (type === "MAILCHIMP") {
      return (
        <img src={`${appUrl}/static/images/mailchimp.svg`} className="w-4 rounded-full" alt="" />
      );
    }

    // Fallback: entity icon from inputType
    if (inputType === "SALE") return <CurrencyDollarIcon className="w-4" />;
    if (inputType === "CONTACT") return <UserIcon className="w-4" />;
    if (inputType === "COMPANY") return <BuildingOffice2Icon className="w-4" />;
    if (inputType === "PRODUCT") return <CubeIcon className="w-4" />;
  }

  // ── Events/Triggers: entity-based icons ──
  if (type === "EMAIL") return <EnvelopeIcon className="w-4" />;
  if (type === "SLACK") {
    return <SlackSVG className="w-4 rounded-full" />;
  }
  if (type === "MAILCHIMP") {
    return (
      <img src={`${appUrl}/static/images/mailchimp.svg`} className="w-4 rounded-full" alt="" />
    );
  }
  if (type === "JIRA" || type === "JSM") {
    return <img src={`${appUrl}/static/images/jsm.svg`} className="w-4 rounded-full" alt="" />;
  }
  if (type === "HUBSPOT") {
    return <img src={`${appUrl}/static/images/hubspot.png`} className="w-4" alt="" />;
  }
  if (type === "GOOGLE") {
    if (itemType === RuleItemTypes.EVENT) {
      return <img src={`${appUrl}/static/images/gmail.png`} className="w-4" alt="" />;
    }
    return <CircleStackIcon className="w-4" />;
  }
  if (type === "CSV") {
    if (itemType === RuleItemTypes.EVENT) {
      return <CloudArrowUpIcon className="w-4" />;
    }
    return <CircleStackIcon className="w-4" />;
  }
  if (type === "SALE") return <CurrencyDollarIcon className="w-4" />;
  if (type === "CONTACT") return <UserIcon className="w-4" />;
  if (type === "COMPANY") return <BuildingOffice2Icon className="w-4" />;
  if (type === "PRODUCT") return <CubeIcon className="w-4" />;
  if (type === "ACTIVITY") return <CalendarIcon className="w-4" />;

  return null;
}
